import PageHeader from '@components/PageHeader'
import './index.scss'

import planningCenterLogoWhite from 'url:@image/planning-center-white.svg'
import planningCenterLogoColor from 'url:@image/planning-center-full-color.svg'

import smartWaiverLogo from 'url:@image/smartwaiver-logo.svg'

import useDarkMode from '@hooks/useDarkMode'
import { useEffect, useState } from 'react'
import { useAuth } from '@auth/AuthProvider'
import { useChurchDataProvider } from '@data/ChurchDataProvider'
import ConnectionsController, { ExternalVendor, ConnectionStatusResponse } from '@data/controller/ConnectionsController'
import LoadingIndicator from '@components/LoadingIndicator'
import Modal, { ModalButtonStyle } from '@components/Modal'

type APIKeyInstructionsContainer = {
  instructions: string;
  provider: ExternalVendor;
}

export default function Connections() {
  const auth = useAuth();
  const darkMode = useDarkMode();

  const { connectionStatus, reloadConnectionStatus } = useChurchDataProvider();
  const [loadingConnectionInfo, setLoadingConnectionInfo] = useState(false);

  const [showAPIKeyModal, setShowAPIKeyModal] = useState(false);
  const [savingAPIKey, setSavingAPIKey] = useState(false);
  const [apiKeyInstructions, setAPIKeyInstructions] = useState<APIKeyInstructionsContainer | null>(null);

  const fetchConnectionSetupInformation = async (provider: ExternalVendor) => {
    if (!auth.churchId || loadingConnectionInfo) return;
    setLoadingConnectionInfo(true);

    try {
      const response = await ConnectionsController.fetchConfigureConnectionURLForProvider(auth.churchId, provider, auth);

      if (!response.body) {
        window.alert("Failed to load configuration information for provider. Please try again later.");
        console.log(response.error);
        return;
      }

      const body = response.body;

      if (body.authMethod === 'oauth') {
        const authURL = body.authDetails.url;
        window.location.href = authURL;
      } else if (body.authMethod === 'apiKey') {
        const instructions = body.authDetails.instructions;
        setAPIKeyInstructions({
          instructions,
          provider
        });
      }
    } finally {
      setLoadingConnectionInfo(false);
    }
  }

  const saveAPIKeyForSelectedProvider = async (apiKey: string) => {
    if (!auth.churchId || !apiKeyInstructions) return;
    setSavingAPIKey(true);
    
    try {
      const response = await ConnectionsController.saveAPIKeyForProvider(auth.churchId, apiKeyInstructions.provider, apiKey, auth);
      
      if (response.error) {
        window.alert(response.error);
        console.log(response.error);
      } else {
        setShowAPIKeyModal(false);
        reloadConnectionStatus();
      }
    } catch {
      window.alert("Failed to save API Key. Please try again later.");
    } finally {
      setSavingAPIKey(false);
    }
  };

  useEffect(() => {
    // Ensure we have the latest connection status when this page mounts
    reloadConnectionStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (apiKeyInstructions !== null) {
      setShowAPIKeyModal(true);
    }
  }, [apiKeyInstructions]);

  useEffect(() => {
    if (!showAPIKeyModal) {
      setTimeout(() => {
        setAPIKeyInstructions(null);
      }, 400);
    }
  }, [showAPIKeyModal]);

  if (!connectionStatus) {
    return <div className='connections-container'>
      <LoadingIndicator />
    </div>
  }

  return <div className='connections-container'>
    <PageHeader title='Connections'
      showBackButton={false} />

    <APIKeyConnectionInstructionsModal isOpen={showAPIKeyModal}
      instructions={apiKeyInstructions}
      close={() => {
        setShowAPIKeyModal(false);
      }}
      save={(apiKey) => {
        saveAPIKeyForSelectedProvider(apiKey);
      }}
      isSaving={savingAPIKey} />

    <div className='cards'>
      <ConnectionCard
        logo={darkMode ? planningCenterLogoWhite : planningCenterLogoColor}
        description='Connecting with Planning Center enables your church to sync Planning Center registrations with Salt Events.'
        isConnected={connectionStatus[ExternalVendor.PLANNING_CENTER]}
        attemptingConnection={loadingConnectionInfo}
        connectButtonClicked={() => {
          fetchConnectionSetupInformation(ExternalVendor.PLANNING_CENTER);
        }} />

      <ConnectionCard
        logo={smartWaiverLogo}
        description='Connecting with Smartwaiver allows completed waivers to be automatically linked to event registrations in Salt Events.'
        isConnected={connectionStatus[ExternalVendor.SMART_WAIVER]}
        attemptingConnection={loadingConnectionInfo}
        connectButtonClicked={() => {
          fetchConnectionSetupInformation(ExternalVendor.SMART_WAIVER);
        }} />
    </div>
  </div>
}

function ConnectionCard(props: {
  logo: any,
  description: string,
  isConnected: boolean,
  attemptingConnection: boolean,
  connectButtonClicked: () => void
}) {
  return <div className='connection-card card'>
    <div className='header'>
      <img src={props.logo} />
    </div>

    <p className='description'>{props.description}</p>
    {
      props.isConnected ?
        <p className='connected-text'><span className='connected-dot' aria-hidden="true" /> Connected</p> :
        <button onClick={props.connectButtonClicked} disabled={props.attemptingConnection}>
          Connect
        </button>
    }
  </div>
}

function APIKeyConnectionInstructionsModal(props: {
  isOpen: boolean,
  instructions: APIKeyInstructionsContainer | null,
  close: () => void,
  save: (apiKey: string) => void,
  isSaving: boolean
}) {
  const [apiKey, setAPIKey] = useState('');

  return <Modal id="apiKey" isOpen={props.isOpen} footerButtons={[
    {
      title: 'Cancel',
      style: ModalButtonStyle.OUTLINE,
      onClick: props.close,
      enabled: !props.isSaving
    },
    {
      title: 'Save',
      style: ModalButtonStyle.FILLED,
      onClick: () => {
        props.save(apiKey);
      },
      enabled: apiKey.length > 0 && !props.isSaving,
    }
  ]}>
    {props.instructions ?
      <div style={{ padding: '4px 0 16px 0' }}>
        <h3>Instructions</h3>
        <p dangerouslySetInnerHTML={{ __html: props.instructions.instructions.replace(/\n/g, '<br />') }}></p>

        <br />

        <input id='apiKey' type="text" placeholder='API Key' style={{
          width: '100%',
          boxSizing: 'border-box',
          height: '48px',
          border: 'none',
          borderRadius: '8px',
          paddingLeft: '16px',
          fontFamily: 'monospace',
        }}
          value={apiKey}
          onChange={(e) => {
            setAPIKey(e.target.value);
          }} />
      </div>
      : null}
  </Modal>
}